import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Coins,
  Globe2,
  Hash,
  Landmark,
  Pencil,
  Save,
  Trash2,
  Truck,
  X,
} from "lucide-react";

import {
  deleteExportCottages,
  fetchExportCottageDetails,
  updateExportCottageDetails,
} from "../actions/cottageActions";
import ExportedCottageGoodsList from "../components/ExportedCottageGoodsList";
import ConfirmModal from "../components/ui/ConfirmModal";
import HotToastMessage from "../components/ui/HotToastMessage";
import { LazyLoading } from "../components/ui/LazyLoading";

const cx = (...classes) => classes.filter(Boolean).join(" ");
const formatNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(number)
    ? "—"
    : number.toLocaleString("fa-IR", { maximumFractionDigits: 2 });
};

const cardClass = "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm";
const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900 shadow-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/25";

function Card({ title, icon: Icon, action, className = "", children }) {
  return (
    <section className={cx(cardClass, className)}>
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white px-5 py-4">
        {action || <span />}
        <div className="ml-auto flex items-center gap-3">
          {Icon && <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Icon size={20} /></span>}
          <h2 className="text-base font-extrabold text-slate-800 sm:text-lg">{title}</h2>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-3 last:border-b-0">
      <div className="flex shrink-0 items-center gap-2 text-slate-600">
        {Icon && <Icon size={16} className="text-slate-400" />}
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <div className="break-words text-left text-sm text-slate-900">{value ?? "—"}</div>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", step }) {
  return (
    <label className="block space-y-1.5 text-sm font-medium text-slate-700">
      <span>{label}</span>
      <input className={inputClass} type={type} step={step} value={value ?? ""} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

const STATUS_OPTIONS = ["پروانه کامل", "تایید شده", "در حال بررسی", "رد شده"];
const CURRENCY_OPTIONS = ["دلار امریکا", "دلار آمریکا", "یورو", "ریال ایران"];

const ExportedCottageDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fullSerialNumber } = useParams();
  const { loading, cottage, error } = useSelector((state) => state.exportedCottageDetails);

  const [isEditing, setIsEditing] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [cottageDate, setCottageDate] = useState(null);
  const [totalValue, setTotalValue] = useState("");
  const [remainingTotal, setRemainingTotal] = useState("");
  const [quantity, setQuantity] = useState("");
  const [status, setStatus] = useState("");
  const [currencyType, setCurrencyType] = useState("");
  const [currencyPrice, setCurrencyPrice] = useState("");

  useEffect(() => {
    if (fullSerialNumber) dispatch(fetchExportCottageDetails(fullSerialNumber));
  }, [dispatch, fullSerialNumber]);

  useEffect(() => {
    if (!cottage) return;
    setTotalValue(cottage.total_value ?? "");
    setRemainingTotal(cottage.remaining_total ?? "");
    setQuantity(cottage.quantity ?? "");
    setStatus(cottage.declaration_status ?? "");
    setCurrencyType(cottage.currency_type ?? "");
    setCurrencyPrice(cottage.currency_price ?? "");
    setCottageDate(
      cottage.cottage_date
        ? new DateObject({ date: cottage.cottage_date, format: "YYYY-MM-DD", calendar: persian, locale: persianFa })
        : null
    );
  }, [cottage]);

  const activeGoods = useMemo(
    () => (cottage?.cottage_goods || []).filter((good) => good.is_active),
    [cottage?.cottage_goods]
  );

  const handleSave = async () => {
    await dispatch(updateExportCottageDetails(cottage.id, {
      cottage_date: cottageDate?.format("YYYY-MM-DD") || null,
      total_value: totalValue,
      remaining_total: remainingTotal === "" ? null : remainingTotal,
      quantity,
      declaration_status: status,
      currency_price: currencyPrice === "" ? null : currencyPrice,
      currency_type: currencyType,
    }, fullSerialNumber));
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    try {
      await dispatch(deleteExportCottages([cottage.id]));
      navigate("/export-cottages");
    } catch (deleteError) {
      setToast({ message: "حذف اظهارنامه انجام نشد.", type: "error" });
    }
  };

  if (loading) return <LazyLoading />;

  if (error) {
    const message = typeof error === "string" ? error : JSON.stringify(error);
    return (
      <div className="mt-24 px-3 font-vazirmatn sm:px-6" dir="rtl">
        <div className="mx-auto max-w-6xl rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">
          <p className="font-bold">خطا در دریافت جزئیات اظهارنامه</p>
          <p className="mt-2 break-words text-sm">{message}</p>
          <button type="button" onClick={() => dispatch(fetchExportCottageDetails(fullSerialNumber))} className="mt-4 rounded-xl bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-800">
            تلاش دوباره
          </button>
        </div>
      </div>
    );
  }

  if (!cottage) {
    return (
      <div className="mt-24 px-3 font-vazirmatn sm:px-6" dir="rtl">
        <div className="mx-auto max-w-6xl rounded-2xl border border-slate-200 bg-white p-5 text-slate-700">اظهارنامه‌ای با این شماره پیدا نشد.</div>
      </div>
    );
  }

  return (
    <div className="mt-20 px-3 pb-16 font-vazirmatn sm:mt-24 sm:px-6" dir="rtl">
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />
      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      <div className="mx-auto max-w-6xl">
        <header className={cardClass}>
          <div className="flex flex-col gap-4 bg-gradient-to-b from-slate-50 to-white px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary"><ClipboardList size={22} /></span>
              <div>
                <h1 className="text-lg font-extrabold text-slate-800 sm:text-xl">جزئیات اظهارنامه صادراتی</h1>
                <p className="mt-1 text-sm text-slate-500">شماره سریال: <span className="font-bold text-slate-700">{cottage.full_serial_number}</span></p>
              </div>
            </div>
            <Link to="/export-cottages" className="inline-flex items-center gap-2 self-start rounded-xl px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-100 sm:self-center">
              <ArrowRight size={18} /> بازگشت به اظهارنامه‌ها
            </Link>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
            {isEditing ? (
              <>
                <button type="button" onClick={handleSave} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-95">
                  <Save size={18} /> ذخیره تغییرات
                </button>
                <button type="button" onClick={() => setIsEditing(false)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
                  <X size={18} /> انصراف
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setIsEditing(true)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-95">
                <Pencil size={18} /> ویرایش
              </button>
            )}
            <button type="button" onClick={() => setConfirmDeleteOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-rose-700">
              <Trash2 size={18} /> حذف اظهارنامه
            </button>
          </div>
        </header>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card title="اطلاعات اظهارنامه" icon={Hash} className="lg:col-span-2">
            {isEditing ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block space-y-1.5 text-sm font-medium text-slate-700">
                  <span>تاریخ اظهار</span>
                  <DatePicker
                    value={cottageDate}
                    onChange={setCottageDate}
                    calendar={persian}
                    locale={persianFa}
                    format="YYYY/MM/DD"
                    inputClass={inputClass}
                    calendarPosition="bottom-right"
                  />
                </label>
                <label className="block space-y-1.5 text-sm font-medium text-slate-700">
                  <span>وضعیت اظهارنامه</span>
                  <select className={inputClass} value={status} onChange={(event) => setStatus(event.target.value)}>
                    {![...new Set([status, ...STATUS_OPTIONS].filter(Boolean))].map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>
                <label className="block space-y-1.5 text-sm font-medium text-slate-700">
                  <span>نوع ارز</span>
                  <select className={inputClass} value={currencyType} onChange={(event) => setCurrencyType(event.target.value)}>
                    {![...new Set([currencyType, ...CURRENCY_OPTIONS].filter(Boolean))].map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>
                <EditField label="تعداد کالاها" type="number" value={quantity} onChange={setQuantity} />
                <EditField label="ارزش اظهارنامه" type="number" step="any" value={totalValue} onChange={setTotalValue} />
                <EditField label="مانده" type="number" step="any" value={remainingTotal} onChange={setRemainingTotal} />
                <EditField label="نرخ ارز" type="number" step="any" value={currencyPrice} onChange={setCurrencyPrice} />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <InfoRow label="شماره سریال" value={cottage.full_serial_number} icon={Hash} />
                  <InfoRow label="تاریخ اظهار" value={String(cottage.cottage_date || "—").replaceAll("-", "/")} icon={CalendarDays} />
                  <InfoRow label="تاریخ پرداخت" value={String(cottage.cottage_payment_date || "—").replaceAll("-", "/")} icon={CalendarDays} />
                  <InfoRow label="وضعیت" value={cottage.declaration_status || "—"} icon={CheckCircle2} />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <InfoRow label="گمرک اظهار" value={cottage.customs_name} icon={Landmark} />
                  <InfoRow label="گمرک خروج" value={cottage.exit_customs_name} icon={Landmark} />
                  <InfoRow label="کشور مقصد" value={cottage.destination_country} icon={Globe2} />
                  <InfoRow label="روش حمل" value={cottage.transport_method} icon={Truck} />
                </div>
              </div>
            )}
          </Card>

          <Card title="ارزش و مقادیر" icon={Coins}>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <InfoRow label="ارزش کل" value={`${formatNumber(cottage.total_value)} ${cottage.currency_type || ""}`} />
              <InfoRow label="ارزش ریالی اظهارنامه" value={formatNumber(cottage.customs_value_epl)} />
              <InfoRow label="مانده" value={formatNumber(cottage.remaining_total)} />
              <InfoRow label="تعداد کالاها" value={formatNumber(cottage.quantity)} />
              <InfoRow label="نرخ ارز" value={formatNumber(cottage.currency_price)} />
              <InfoRow label="کد اظهارنامه NTSW" value={cottage.ntsw_declaration_vcode} />
            </div>
          </Card>
        </div>

        <div className="mt-4">
          <Card
            title="کالاهای اظهارنامه"
            icon={ClipboardList}
            action={<span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{activeGoods.length.toLocaleString("fa-IR")} قلم</span>}
          >
            {activeGoods.length ? (
              <ExportedCottageGoodsList goods={activeGoods} currency={cottage.currency_type} />
            ) : (
              <p className="text-sm text-slate-600">کالایی برای این اظهارنامه ثبت نشده است.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ExportedCottageDetails;
